import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPages.css';

const TermsPartners = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <Link to="/" className="back-home">← Back to Home</Link>

        <h1 className="legal-title">Dangol Business Terms of Service</h1>
        <p className="legal-last-updated">Last updated: September 30, 2026</p>

        <div className="legal-content">
          <p>
            Welcome to the Dangol platform for businesses ("Dangol," "we," "us," or "our"). This Business Terms of Service ("Terms") is a binding legal agreement between you, the business entity you represent ("you," "Business," or "Merchant"), and Dangol LLC.
          </p>

          <p>
            This agreement governs your use of the Dangol business mobile application, web dashboard, kiosk software, and all related services (collectively, the "Service"). By creating a business account or using the Service, you agree to these Terms.
          </p>

          <h2>1. Definitions</h2>
          <p>
            <strong>"Business Account":</strong> The primary account created by the owner or authorized representative of a Merchant to manage their loyalty program and/or Events.
          </p>
          <p>
            <strong>"Authorized User":</strong> The individual who creates and manages the Business Account, such as the shop owner or manager.
          </p>
          <p>
            <strong>"Event Coordinator":</strong> A Business that creates and hosts Events (free or paid) through the Service. References to "Business" or "Merchant" in these Terms include Event Coordinators where applicable.
          </p>
          <p>
            <strong>"Event":</strong> An event you list on the Service. A "Paid Event" is an Event that requires a payment, such as a ticket, to attend.
          </p>
          <p>
            <strong>"Kiosk User":</strong> An employee or agent of the Merchant who is authorized to use the kiosk functionality of the Service via a PIN.
          </p>
          <p>
            <strong>"Merchant Content":</strong> Any text, images, logos, data, or other materials that the Merchant uploads or creates within the Service, including notification content, Event listings, and branding assets.
          </p>
          <p>
            <strong>"Customer Data":</strong> Information related to a customer's activity at your specific shop or Events, including scan history, rewards progress, and Event attendance.
          </p>

          <h2>2. Business Accounts & Security</h2>
          <p>To use the Service, you must be an authorized representative of a legitimate business and at least 18 years of age.</p>
          <p>You are responsible for:</p>
          <ul>
            <li>Providing accurate and complete information when creating your Business Account.</li>
            <li>Maintaining the confidentiality of your primary account password.</li>
            <li>All activity that occurs under your Business Account, including all actions taken by your Kiosk Users.</li>
            <li>Creating, managing, and maintaining the security of all Kiosk User PINs. You agree to instruct your Kiosk Users on the proper and lawful use of the Service.</li>
            <li>You acknowledge and agree that we may implement automated security measures on the Service, including, but not limited to, rate-limiting and the temporary or permanent locking of a Kiosk User PIN after excessive failed login attempts. You agree that the sole method for unlocking a locked Kiosk PIN is for the Authorized User to reset it through the Service.</li>
          </ul>

          <h2>3. The Service & Data Analytics</h2>
          <p>We grant you a limited, non-exclusive, non-transferable license to access and use the Service for your internal business purposes.</p>
          <p>The Service includes access to an analytics dashboard. You acknowledge and agree that:</p>
          <ul>
            <li>You own the Customer Data for your specific shop.</li>
            <li>The Service provides analytics based on this Customer Data to you for your business insights. You may only use this data for lawful purposes.</li>
            <li>For Events, the Service provides you with aggregate, non-identifying information and trends about attendance and engagement. Except as described in Section 6 (Raffles and Promotions), the Service does not provide you with attendees' personal contact information (such as email addresses or phone numbers).</li>
            <li>We have the right to use Customer Data in an aggregated and anonymized form for the purpose of improving the Dangol platform and for statistical analysis. We will never share your identifiable Customer Data with other Merchants.</li>
          </ul>

          <h2>4. Rewards Program</h2>
          <p>Dangol provides the platform for the loyalty program, but the rewards themselves are offered directly by the individual Shops.</p>
          <p>
            <strong>Responsibility:</strong> The terms, conditions, fulfillment, and value of any reward are determined solely by the participating Business. Dangol is not a party to the transaction between the Customer and the Business and is not responsible for a Business's failure to honor a reward.
          </p>
          <p>
            <strong>Rule Changes:</strong> A Business may change its reward rules at any time. The Dangol service is designed to honor the rules that were in effect for a Customer at the start of their current reward cycle. Rewards have no cash value and are not transferable.
          </p>

          <h2>5. Events and Ticketing</h2>
          <p>
            The Service allows eligible Businesses and Event Coordinators to create, promote, and manage Events, including free Events and Paid Events.
          </p>
          <p>
            <strong>Your Responsibility:</strong> You are solely responsible for your Events, including their content, scheduling, pricing, admission, capacity, safety, cancellation, and conduct, and for obtaining any permits, licenses, insurance, and approvals and complying with all applicable laws. Dangol does not host, organize, or run your Events.
          </p>
          <p>
            <strong>Assumption of Risk:</strong> You acknowledge that attendees participate in your Events at their own risk, and that Dangol is not responsible or liable for any injury, loss, or damage arising out of or related to your Events. You are responsible for the safety and conduct of your Events and for obtaining any insurance appropriate to the risks involved.
          </p>
          <p>
            <strong>Payments:</strong> Payments for Paid Events are processed by a third-party payment processor (such as Stripe) and are paid directly to you. You are the merchant and seller of record for your Events. Dangol does not collect, hold, process, or control funds for your Events. You are responsible for setting prices, determining and remitting any applicable taxes, and complying with the payment processor's terms and all applicable laws.
          </p>
          <p>
            <strong>Refunds and Cancellations:</strong> You are solely responsible for your refund and cancellation policies and for handling all refund requests, chargebacks, and related disputes with attendees. Dangol does not issue refunds and is not responsible for your refund or cancellation practices.
          </p>
          <p>
            <strong>Attendee Information:</strong> Dangol does not provide you with attendees' personal contact information (such as email addresses or phone numbers), except for the winner of a Promotion as described in Section 6. Any customer information you receive through the Service may be used only for the purpose for which it was provided and must be handled in compliance with applicable law.
          </p>

          <h2>6. Raffles and Promotions</h2>
          <p>
            You may offer raffles, giveaways, or other promotions through the Service ("Promotions"), including Promotions with prizes funded by you or by Dangol.
          </p>
          <p>
            <strong>Sponsor Responsibility:</strong> You are responsible for each Promotion you create or run, including establishing any official rules, determining eligibility, selecting and notifying winners, and delivering prizes. You are solely responsible for ensuring the Promotion complies with all applicable laws and regulations, including those governing contests, sweepstakes, raffles, and gambling, regardless of whether the prize is funded by you or by Dangol. Promotions are void where prohibited.
          </p>
          <p>
            <strong>Winner Information:</strong> To enable you to deliver a prize, Dangol may provide you with the winner's name and email address. You may use this information solely to fulfill the prize and must handle it in accordance with applicable law.
          </p>

          <h2>7. Notifications</h2>
          <p>Businesses and Event Coordinators may send promotional and event-related notifications to their Customers through the Service.</p>
          <p>
            <strong>Customer Choice:</strong> Customers can opt out of receiving promotional notifications from any specific Shop or Event Coordinator at any time through the settings in the Customer App.
          </p>
          <p>
            <strong>Business Responsibility:</strong> Businesses are solely responsible for the content, accuracy, legality, and scheduling of the notifications they send. Dangol is a platform provider and does not endorse or verify the content of these notifications.
          </p>

          <h2>8. User Conduct</h2>
          <p>
            You agree not to use the Service for any unlawful purpose or in any way that could damage, disable, or impair the service. Businesses agree not to send notifications that are deceptive, malicious, or violate any applicable laws.
          </p>

          <h2>9. Intellectual Property</h2>
          <p>
            The Service and its original content, features, and functionality are owned by Dangol and are protected by international copyright, trademark, and other intellectual property laws.
          </p>
          <p>
            <strong>Merchant Content License:</strong> You retain ownership of your Merchant Content. You grant Dangol a limited, non-exclusive, worldwide, royalty-free license to host, store, reproduce, and display your Merchant Content solely for the purpose of operating and providing the Service, including displaying your branding, Event listings, and notifications to Customers.
          </p>

          <h2>10. Disclaimers</h2>
          <p>
            The Service is provided on an "AS IS" and "AS AVAILABLE" basis. Dangol makes no warranties, expressed or implied, regarding the reliability, security, or availability of the Service. Dangol does not host Events, sell tickets, or process payments, and makes no warranties regarding any Event, Promotion, or attendee.
          </p>

          <h2>11. Limitation of Liability</h2>
          <p>
            In no event shall Dangol LLC be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill, arising from your use of the Service; the conduct of any Business or Customer; or any Event or Promotion you host or run.
          </p>

          <h2>12. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Dangol LLC and its officers, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or related to: (a) your use of the Service; (b) any Event or Promotion you host, run, or offer; (c) your Merchant Content; (d) your products, services, rewards, or interactions with Customers; or (e) your violation of these Terms or any applicable law.
          </p>

          <h2>13. Termination</h2>
          <p>
            We may terminate or suspend your account and access to the Service immediately, without prior notice, for any reason, including a breach of these Terms.
          </p>

          <h2>14. Governing Law</h2>
          <p>
            These Terms shall be governed and construed in accordance with the laws of the State of Michigan, United States, without regard to its conflict of law provisions.
          </p>

          <h2>15. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. We will provide the latest version of these Terms in the Service. Your continued use of the Service after such changes constitutes your acceptance of the new Terms.
          </p>

          <h2>16. Contact Information</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us at <a href="mailto:partnersupport@dangolapp.com">partnersupport@dangolapp.com</a>.
          </p>
        </div>

        <div className="legal-footer">
          <Link to="/privacy-partners">Privacy Policy for Partners</Link>
          <span> | </span>
          <Link to="/terms">Terms of Service for Customers</Link>
          <span> | </span>
          <Link to="/">Home</Link>
        </div>
      </div>
    </div>
  );
};

export default TermsPartners;
